import React,{useState,useMemo,useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {PageHeader,DataTable,Modal,Badge} from '../components/ui/SharedUI';
import TenantForm from '../components/forms/TenantForm';
import {demoRepository} from '../repository/demoRepository';
import {calculations} from '../utils/calculations';
import {rentScheduleService} from '../services/rentScheduleService';
import {useFeedback} from '../components/ui/FeedbackUI';
import {SAFE_DOCUMENT_TYPES} from '../config/documentTypes';
import SmartDateInput from '../components/ui/SmartDateInput';
import SafeIcon from '../common/SafeIcon';
import {isContractualLease} from '../utils/leasePosition';

const toLocalDateStamp=(value)=> {
  if (!value) return null;
  if (typeof value==='string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year,month,day]=value.split('-').map(Number);
    const localDate=new Date(year,month - 1,day);
    return Number.isNaN(localDate.getTime()) ? null : localDate.getTime();
  }
  const date=value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(),date.getMonth(),date.getDate()).getTime();
};

const toDateInputValue=(value)=> {
  const stamp=toLocalDateStamp(value);
  if (stamp===null) return '';
  const date=new Date(stamp);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
};

const getTenantDisplayName=(tenant)=> {const fullName=[tenant?.firstName,tenant?.lastName].filter(Boolean).join(' ').trim();return fullName || String(tenant?.name || '').trim() || 'Unnamed tenant';};

const appendNote=(existing,note)=> {
  const base=String(existing || '').trim();
  if (!note || base.includes(note)) return base;
  return [base,note].filter(Boolean).join('\n');
};

const getFinancialYear=(value)=> {
  const stamp=toLocalDateStamp(value);
  if (stamp===null) return '';
  const date=new Date(stamp);
  const year=date.getFullYear();
  return date.getMonth() >=6 ? `${year}-${String(year + 1).slice(-2)}` : `${year - 1}-${String(year).slice(-2)}`;
};

const findOverlappingActiveLease=(candidateLease,allLeases,excludeLeaseId=null)=> {
  const propertyId=String(candidateLease?.propertyId || candidateLease?.property || '');
  const spaceId=String(candidateLease?.spaceId || candidateLease?.space || '');
  const start=toLocalDateStamp(candidateLease?.startDate || candidateLease?.leaseStart);
  const end=toLocalDateStamp(candidateLease?.endDate || candidateLease?.leaseEnd);
  if (!propertyId || start===null || end===null) return null;
  return (allLeases || []).find(existing=> {
    if (!existing || String(existing.id || '')===String(excludeLeaseId || '')) return false;
    if (!isContractualLease(existing)) return false;
    if (String(existing.propertyId || existing.property || '') !==propertyId) return false;
    const existingSpaceId=String(existing.spaceId || existing.space || '');
    const sameRentalScope=!spaceId || !existingSpaceId || spaceId===existingSpaceId;
    if (!sameRentalScope) return false;
    const existingStart=toLocalDateStamp(existing.startDate || existing.leaseStart);
    const existingEnd=toLocalDateStamp(existing.endDate || existing.leaseEnd);
    if (existingStart===null || existingEnd===null) return false;
    return start <=existingEnd && end >=existingStart;
  }) || null;
};

const TenantsPage=()=> {
  const navigate=useNavigate();
  const {showToast}=useFeedback();
  const [isAddModalOpen,setIsAddModalOpen]=useState(false);
  const [searchTerm,setSearchTerm]=useState('');
  const [propertyFilter,setPropertyFilter]=useState('All');
  const [statusFilter,setStatusFilter]=useState('Active');
  const [refreshTrigger,setRefreshTrigger]=useState(0);
  const [isSavingTenant,setIsSavingTenant]=useState(false);
  const [isTerminateModalOpen,setIsTerminateModalOpen]=useState(false);
  const [terminationTarget,setTerminationTarget]=useState(null);
  const [isTerminating,setIsTerminating]=useState(false);
  const [terminationForm,setTerminationForm]=useState({
    terminationDate: '',
    reason: 'Lease broken by tenant',
    settlementAmount: '',
    settlementDate: '',
    notes: ''
  });

  useEffect(()=> {
    const handleRefresh=()=> setRefreshTrigger(prev=> prev + 1);
    window.addEventListener('mpp:data-refreshed',handleRefresh);
    return ()=> window.removeEventListener('mpp:data-refreshed',handleRefresh);
  },[]);

  const properties=demoRepository.getAllProperties().filter(p=> p.status==='Active');
  const allSpaces=demoRepository.getPortfolio().totalSpaces > 0 ? properties.flatMap(p=> demoRepository.getSpacesByProperty(p.id)) : [];

  const tenantsWithLeases=useMemo(()=> {
    const tenants=demoRepository.getAllTenants();
    return tenants.map(tenant=> {
      const leasePosition=demoRepository.getLeasePositionByTenantId(tenant.id);
      const currentLease=leasePosition.current || null;
      const nextLease=leasePosition.next || null;
      const lease=currentLease || nextLease || leasePosition.previous || null;
      const property=lease ? demoRepository.getPropertyById(lease.propertyId) : null;
      const space=lease && lease.spaceId ? demoRepository.getSpaceById(lease.spaceId) : null;
      const tenure=calculations.calculateTenure(lease?.startDate);
      const daysToExpiry=calculations.calculateDaysToExpiry(lease?.endDate);
      const openItemsCount=demoRepository.getAllOpenItems().filter(oi=> oi.status !=='Resolved' && oi.status !=='Archived').length;
      return {tenant,lease,currentLease,nextLease,leasePosition,property,space,tenure,daysToExpiry,openItemsCount,annualizedRent: calculations.calculateAnnualizedRent(lease?.weeklyRent,lease?.rentBasis)};
    }).filter(item=> {
      if (statusFilter !=='All' && item.tenant.status !==statusFilter) return false;
      if (propertyFilter !=='All' && item.property?.id !==propertyFilter) return false;
      if (searchTerm) {
        const lower=searchTerm.toLowerCase();
        return (getTenantDisplayName(item.tenant).toLowerCase().includes(lower) || item.property?.name?.toLowerCase().includes(lower) || item.space?.name?.toLowerCase().includes(lower));
      }
      return true;
    });
  },[searchTerm,propertyFilter,statusFilter,refreshTrigger]);

  const tenantListSummary=useMemo(()=> {
    return tenantsWithLeases.reduce((summary,item)=> {
      if (item.currentLease) summary.activeLeases += 1;
      if (!item.lease) summary.withoutLease += 1;
      if (Number.isFinite(Number(item.daysToExpiry)) && item.daysToExpiry >=0 && item.daysToExpiry <=60) summary.endingSoon += 1;
      return summary;
    },{activeLeases: 0,endingSoon: 0,withoutLease: 0});
  },[tenantsWithLeases]);

  const openTerminationModal=(item)=> {
    const today=toDateInputValue(Date.now());
    setTerminationTarget(item);
    setTerminationForm({
      terminationDate: today,
      reason: 'Lease broken by tenant',
      settlementAmount: '',
      settlementDate: today,
      notes: ''
    });
    setIsTerminateModalOpen(true);
  };

  const closeTerminationModal=()=> {
    if (isTerminating) return;
    setIsTerminateModalOpen(false);
    setTerminationTarget(null);
  };

  const handleTerminateLease=async (event)=> {
    event.preventDefault();
    if (isTerminating || !terminationTarget?.lease || !terminationTarget?.tenant) return;

    const lease=terminationTarget.lease;
    const tenant=terminationTarget.tenant;
    const terminationDate=toLocalDateStamp(terminationForm.terminationDate);
    const leaseStart=toLocalDateStamp(lease.startDate || lease.leaseStart);
    const originalLeaseEnd=toLocalDateStamp(lease.endDate || lease.leaseEnd);
    if (terminationDate===null) {
      showToast('Enter a valid termination date.','error');
      return;
    }
    if (leaseStart!==null && terminationDate < leaseStart) {
      showToast('Termination date cannot be before the lease start date.','error');
      return;
    }
    if (originalLeaseEnd!==null && terminationDate >= originalLeaseEnd) {
      showToast('Early termination date must be before the current contractual lease end date.','error');
      return;
    }

    const settlementAmount=Number(terminationForm.settlementAmount || 0);
    if (!Number.isFinite(settlementAmount) || settlementAmount < 0) {
      showToast('Settlement amount must be zero or a positive number.','error');
      return;
    }
    const settlementDate=settlementAmount > 0 ? toLocalDateStamp(terminationForm.settlementDate || terminationForm.terminationDate) : null;
    if (settlementAmount > 0 && settlementDate===null) {
      showToast('Enter a valid settlement payment date.','error');
      return;
    }

    const originalEndLabel=originalLeaseEnd!==null ? calculations.formatDateAU(originalLeaseEnd) : 'unknown';
    const terminationLabel=calculations.formatDateAU(terminationDate);
    const tenantName=`${tenant.firstName || ''} ${tenant.lastName || ''}`.trim() || tenant.name || 'Tenant';
    const reason=String(terminationForm.reason || 'Early lease termination').trim();
    const scheduleRows=demoRepository.getRentSchedules().filter(row=> String(row?.leaseId || '')===String(lease.id));
    const changedRows=[];
    let leaseUpdated=false;
    let tenantUpdated=false;

    const rollbackRows=async ()=> {
      for (let index=changedRows.length - 1;index>=0;index--) {
        const originalRow=changedRows[index];
        await demoRepository.updateRentSchedule(originalRow.id,{...originalRow});
      }
    };

    const rollbackTermination=async ()=> {
      try {
        if (tenantUpdated) await demoRepository.updateTenant(tenant.id,{...tenant});
        if (leaseUpdated) await demoRepository.updateLease(lease.id,{...lease});
        await rollbackRows();
      } catch (rollbackError) {
        showToast('Automatic rollback could not fully complete. Please review the tenant, lease and rent schedule before continuing.','error');
      }
    };

    setIsTerminating(true);
    try {
      const futureNote=`Waived from ${terminationLabel} due to early lease termination. Original contractual end: ${originalEndLabel}.`;
      const reviewNote=`Final rent period overlaps the early termination date ${terminationLabel}. Review the actual amount received or owed before marking Paid, Partial or Waived.`;

      for (const row of scheduleRows) {
        const status=String(row?.status || '').trim().toLowerCase();
        const periodStart=toLocalDateStamp(row?.periodStart);
        const periodEnd=toLocalDateStamp(row?.periodEnd);
        const hasPayment=Boolean(row?.paidToDate) || status==='paid' || status==='partial';
        const protectedStatus=['waived','archived','disputed'].includes(status);

        if (periodStart===null || periodEnd===null || hasPayment || protectedStatus) continue;

        if (periodStart > terminationDate) {
          changedRows.push({...row});
          const result=await demoRepository.updateRentSchedule(row.id,{
            ...row,
            status: 'Waived',
            paidToDate: null,
            notes: appendNote(row.notes,futureNote),
            metadata: {
              ...(row.metadata || {}),
              earlyTermination: {
                terminationDate,
                originalLeaseEnd,
                action: 'waived_future_period'
              }
            }
          });
          if (!result?.success) {
            await rollbackRows();
            throw new Error(result?.error || 'A future rent schedule row could not be waived.');
          }
        } else if (periodStart <=terminationDate && periodEnd >=terminationDate) {
          changedRows.push({...row});
          const result=await demoRepository.updateRentSchedule(row.id,{
            ...row,
            notes: appendNote(row.notes,reviewNote),
            metadata: {
              ...(row.metadata || {}),
              earlyTermination: {
                terminationDate,
                originalLeaseEnd,
                action: 'review_final_period'
              }
            }
          });
          if (!result?.success) {
            await rollbackRows();
            throw new Error(result?.error || 'The final overlapping rent period could not be flagged for review.');
          }
        }
      }

      const settlementSummary=settlementAmount > 0
        ? ` Settlement ${calculations.formatCurrencyAUD(settlementAmount)} received ${calculations.formatDateAU(settlementDate)} and recorded separately as other income.`
        : '';
      const extraNotes=String(terminationForm.notes || '').trim();
      const leaseTerminationNote=`Early termination ${terminationLabel}. Original contractual end ${originalEndLabel}. Reason: ${reason}.${settlementSummary}${extraNotes ? ` Notes: ${extraNotes}` : ''}`;
      const leaseResult=await demoRepository.updateLease(lease.id,{
        ...lease,
        status: 'Terminated',
        endDate: terminationDate,
        tenantId: lease.tenantId || tenant.id,
        notes: appendNote(lease.notes,leaseTerminationNote)
      });
      if (!leaseResult?.success) {
        await rollbackRows();
        throw new Error(leaseResult?.error || 'Lease could not be marked as terminated.');
      }
      leaseUpdated=true;

      const remainingLeasePosition=demoRepository.getLeasePositionByTenantId(tenant.id);
      const nextTenantStatus=remainingLeasePosition?.next ? 'Prospective' : 'Former';
      const tenantResult=await demoRepository.updateTenant(tenant.id,{...tenant,status: nextTenantStatus});
      if (!tenantResult?.success) {
        await rollbackTermination();
        throw new Error(tenantResult?.error || 'Tenant could not be marked as former.');
      }
      tenantUpdated=true;

      if (settlementAmount > 0) {
        const alreadyRecorded=demoRepository.getTransactions().some(transaction=>
          String(transaction?.leaseId || '')===String(lease.id) &&
          String(transaction?.metadata?.source || '')==='early_lease_termination' &&
          toLocalDateStamp(transaction?.date || transaction?.transactionDate)===settlementDate &&
          Math.abs(Number(transaction?.amount || 0) - settlementAmount) < 0.005
        );

        if (!alreadyRecorded) {
          const transactionResult=await demoRepository.addTransaction({
            propertyId: lease.propertyId || terminationTarget.property?.id || '',
            spaceId: lease.spaceId || '',
            tenantId: tenant.id,
            leaseId: lease.id,
            date: settlementDate,
            type: 'income',
            category: 'other_income',
            amount: settlementAmount,
            status: 'paid',
            taxCategory: 'accountant_review',
            financialYear: getFinancialYear(settlementDate),
            description: `Early lease termination settlement - ${tenantName}`,
            notes: [reason,extraNotes].filter(Boolean).join(' | '),
            metadata: {
              source: 'early_lease_termination',
              terminationDate,
              originalLeaseEnd,
              tenantName
            }
          });
          if (!transactionResult?.success) {
            await rollbackTermination();
            throw new Error(transactionResult?.error || 'Settlement transaction could not be saved.');
          }
        }
      }

      const waivedCount=changedRows.filter(row=> toLocalDateStamp(row?.periodStart) > terminationDate).length;
      const reviewCount=changedRows.length - waivedCount;
      showToast(`Lease terminated successfully. ${waivedCount} future rent period(s) waived${reviewCount > 0 ? ` and ${reviewCount} final period(s) flagged for review` : ''}.`,'success');
      setIsTerminateModalOpen(false);
      setTerminationTarget(null);
      setRefreshTrigger(prev=> prev + 1);
    } catch (error) {
      showToast(error?.message || 'Early lease termination failed. No intentional history was deleted.','error');
    } finally {
      setIsTerminating(false);
    }
  };

  const handleAddTenant=async (data)=> {
    if (isSavingTenant) return;
    const hasCompleteLease=data.lease && (data.lease.propertyId || data.lease.property) && (data.lease.startDate || data.lease.leaseStart) && (data.lease.endDate || data.lease.leaseEnd);
    if (hasCompleteLease) {
      const conflict=findOverlappingActiveLease(data.lease,demoRepository.getAllLeases());
      if (conflict) {
        const conflictTenant=demoRepository.getTenantById(conflict.tenantId);
        const conflictName=conflictTenant ? getTenantDisplayName(conflictTenant) : 'another tenant';
        const conflictEnd=calculations.formatDateAU(conflict.endDate || conflict.leaseEnd);
        showToast(`Cannot create this lease because it overlaps an active lease for the same property/space${conflictName ? ` (${conflictName})` : ''} ending ${conflictEnd}. Terminate or amend the existing lease first.`,'error');
        return;
      }
    }

    setIsSavingTenant(true);
    try {
      const tenantResult=await demoRepository.addTenant(data.tenant);
      if (!tenantResult?.success) {
        showToast(tenantResult?.error || 'Tenant save failed.','error');
        return;
      }
      const savedTenant=tenantResult.record;
      let savedLease=null;
      let leaseError='';
      if (hasCompleteLease) {
        const leaseResult=await demoRepository.addLease({...data.lease,tenantId: savedTenant.id});
        if (leaseResult?.success) {
          savedLease=leaseResult.record;
        } else {
          leaseError=leaseResult?.error || 'Unknown lease error';
        }
      }

      let scheduleOutcome=null;
      const leaseIsContractual=savedLease ? isContractualLease(savedLease) : false;
      if (savedLease && leaseIsContractual) {
        const existingScheduleRows=demoRepository.getRentSchedules().filter(row=> String(row?.leaseId || '')===String(savedLease.id));
        if (existingScheduleRows.length > 0) {
          scheduleOutcome={status: 'duplicate',count: existingScheduleRows.length};
        } else {
          try {
            const propertyId=savedLease.propertyId || data.lease?.propertyId || data.lease?.property || '';
            const property=propertyId ? demoRepository.getPropertyById(propertyId) : null;
            const activeAssignments=typeof demoRepository.getActivePropertyAgentAssignmentsByProperty==='function'
              ? demoRepository.getActivePropertyAgentAssignmentsByProperty(propertyId)
              : [];
            const managingAssignment=activeAssignments.find(assignment=> String(assignment?.role || '').toLowerCase()==='managing_agent') || activeAssignments[0] || null;
            const recordedManagementFee=managingAssignment?.managementFeePercent ?? property?.managementFeePercent ?? property?.metadata?.agent?.managementFeePercent ?? 0;
            const parsedManagementFee=Number(recordedManagementFee);
            const agentFeePercent=Number.isFinite(parsedManagementFee) && parsedManagementFee >= 0 ? parsedManagementFee : 0;
            const generatedRows=rentScheduleService.generateRentSchedule(savedLease,agentFeePercent,0);

            if (generatedRows.length===0) {
              scheduleOutcome={status: 'not_generated',error: 'Lease rent basis/payment frequency or lease dates could not be scheduled automatically.'};
            } else {
              const tenantDisplayName=`${savedTenant.firstName || ''} ${savedTenant.lastName || ''}`.trim() || savedTenant.name || 'Tenant';
              const scheduleRows=generatedRows.map((row,index)=> ({
                ...row,
                leaseId: savedLease.id,
                propertyId,
                spaceId: savedLease.spaceId || data.lease?.spaceId || data.lease?.space || '',
                tenantId: savedTenant.id,
                tenantDisplayName,
                tenantDisplayMode: '',
                scheduleSource: 'tenant_creation',
                agentFeePercent,
                otherDeductions: 0,
                periodNumber: index + 1,
                metadata: {
                  ...(row.metadata || {}),
                  scheduleSource: 'tenant_creation',
                  tenantDisplayName,
                  tenantDisplayMode: '',
                  propertyId,
                  spaceId: savedLease.spaceId || data.lease?.spaceId || data.lease?.space || '',
                  tenantId: savedTenant.id,
                  leaseId: savedLease.id
                }
              }));
              const scheduleResult=await demoRepository.addRentScheduleRows(scheduleRows);
              if (scheduleResult?.success && (scheduleResult.failedCount || 0)===0) {
                scheduleOutcome={status: 'created',count: scheduleResult.count || scheduleRows.length};
              } else if (scheduleResult?.success) {
                scheduleOutcome={status: 'partial',count: scheduleResult.count || scheduleResult.savedCount || 0,failedCount: scheduleResult.failedCount || 0,error: scheduleResult.errors?.[0] || ''};
              } else {
                scheduleOutcome={status: 'failed',error: scheduleResult?.error || 'Rent schedule save failed.'};
              }
            }
          } catch (scheduleError) {
            scheduleOutcome={status: 'failed',error: scheduleError?.message || 'Rent schedule generation failed.'};
          }
        }
      }

      const stagedDocuments=Array.isArray(data.stagedDocuments) ? data.stagedDocuments : [];
      const successfulUploads=[];
      const failedUploads=[];
      for (const stagedDocument of stagedDocuments) {
        if (!stagedDocument?.file) {
          failedUploads.push({document: stagedDocument,error: 'No file attached'});
          continue;
        }
        try {
          const isSafeDocumentType=SAFE_DOCUMENT_TYPES.includes(stagedDocument.documentType);
          const finalDocumentType=isSafeDocumentType ? stagedDocument.documentType : 'other';
          const intendedDocumentType=isSafeDocumentType ? null : stagedDocument.documentType;
          let documentDate=Date.now();
          if (stagedDocument.documentDate) {
            const parsedDate=new Date(stagedDocument.documentDate).getTime();
            if (!isNaN(parsedDate)) documentDate=parsedDate;
          }
          const fileType=stagedDocument.file.type || stagedDocument.file.name.split('.').pop()?.toUpperCase() || '';
          const payload={
            title: stagedDocument.title || stagedDocument.file.name.replace(/\.[^/.]+$/,''),
            documentType: finalDocumentType,
            documentDate,
            notes: stagedDocument.notes || '',
            status: 'active',
            files: [stagedDocument.file],
            fileName: stagedDocument.file.name,
            fileSize: stagedDocument.file.size,
            fileType,
            uploadedDate: Date.now(),
            tenantId: savedTenant.id,
            propertyId: savedLease?.propertyId || data.lease?.propertyId || data.lease?.property || '',
            leaseId: savedLease?.id || '',
            spaceId: savedLease?.spaceId || data.lease?.spaceId || data.lease?.space || '',
            inspectionId: '',
            metadata: {
              intendedDocumentType,
              uploadedFrom: 'TenantCreationForm',
              tenantId: savedTenant.id,
              leaseId: savedLease?.id || '',
              propertyId: savedLease?.propertyId || data.lease?.propertyId || data.lease?.property || '',
              spaceId: savedLease?.spaceId || data.lease?.spaceId || data.lease?.space || '',
              inspectionId: '',
              fileName: stagedDocument.file.name,
              fileSize: stagedDocument.file.size,
              fileType
            }
          };
          const docResult=await demoRepository.addDocument(payload);
          if (docResult?.success) {
            successfulUploads.push(stagedDocument);
          } else {
            failedUploads.push({document: stagedDocument,error: docResult?.error || 'Document upload failed'});
          }
        } catch (error) {
          failedUploads.push({document: stagedDocument,error: error?.message || 'Document upload failed'});
        }
      }
      if (!hasCompleteLease) {
        if (stagedDocuments.length===0) {
          showToast('Tenant saved successfully.','success');
        } else if (failedUploads.length===0) {
          showToast(`Tenant and ${successfulUploads.length} document(s) saved successfully.`,'success');
        } else if (successfulUploads.length > 0) {
          showToast(`Tenant saved. ${successfulUploads.length} document(s) uploaded and ${failedUploads.length} failed.`,'warning');
        } else {
          showToast('Tenant saved,but all document uploads failed.','error');
        }
      } else if (!leaseError) {
        if (stagedDocuments.length===0) {
          showToast('Tenant and lease saved successfully.','success');
        } else if (failedUploads.length===0) {
          showToast(`Tenant,lease and ${successfulUploads.length} document(s) saved successfully.`,'success');
        } else if (successfulUploads.length > 0) {
          showToast(`Tenant and lease saved. ${successfulUploads.length} document(s) uploaded and ${failedUploads.length} failed.`,'warning');
        } else {
          showToast(`Tenant and lease saved,but all ${failedUploads.length} document upload(s) failed.`,'warning');
        }
      } else {
        if (stagedDocuments.length===0) {
          showToast(`Tenant saved,but the lease failed: ${leaseError}`,'error');
        } else if (successfulUploads.length > 0 && failedUploads.length===0) {
          showToast(`Tenant and ${successfulUploads.length} document(s) saved,but the lease failed: ${leaseError}`,'error');
        } else if (successfulUploads.length > 0) {
          showToast(`Tenant and ${successfulUploads.length} document(s) saved. ${failedUploads.length} document(s) also failed. Lease failed: ${leaseError}`,'error');
        } else {
          showToast(`Tenant saved,but the lease and document uploads failed: ${leaseError}`,'error');
        }
      }

      if (scheduleOutcome?.status==='created') {
        showToast(`${scheduleOutcome.count} rent schedule period(s) created automatically from the active lease.`,'success');
      } else if (scheduleOutcome?.status==='partial') {
        showToast(`Rent schedule was partially created: ${scheduleOutcome.count} period(s) saved and ${scheduleOutcome.failedCount} failed. ${scheduleOutcome.error || ''}`.trim(),'warning');
      } else if (scheduleOutcome?.status==='failed' || scheduleOutcome?.status==='not_generated') {
        showToast(`Tenant and lease remain saved,but the rent schedule was not created: ${scheduleOutcome.error}`,'warning');
      } else if (scheduleOutcome?.status==='duplicate') {
        showToast('An existing rent schedule was found for this lease,so no duplicate schedule was created.','success');
      }

      setIsAddModalOpen(false);
      setRefreshTrigger(prev=> prev + 1);
      navigate(`/tenants/${savedTenant.id}`);
    } finally {
      setIsSavingTenant(false);
    }
  };

  const columns=['Tenant','Property','Space','Lease Start','Lease End','Rent','Bond','Tenure','Status','Actions'];

  const renderRow=(item)=> {
    const tenantDisplayName=getTenantDisplayName(item.tenant);
    const hasValidLeaseEnd = Boolean(item.lease?.endDate && !Number.isNaN(new Date(item.lease.endDate).getTime()));
    const daysToExpiry = hasValidLeaseEnd ? item.daysToExpiry : null;
    let leaseExpiryState = { severity: 'normal', badgeLabel: '', supportingLabel: '' };
    if (daysToExpiry === null) {
      leaseExpiryState = { severity: 'missing', badgeLabel: '', supportingLabel: 'No end date' };
    } else if (daysToExpiry < 0) {
      leaseExpiryState = { severity: 'expired', badgeLabel: 'Expired', supportingLabel: 'Lease has ended' };
    } else if (daysToExpiry === 0) {
      leaseExpiryState = { severity: 'critical', badgeLabel: 'Expires today', supportingLabel: 'Action required' };
    } else if (daysToExpiry <= 30) {
      leaseExpiryState = { severity: 'urgent', badgeLabel: '1 month or less', supportingLabel: 'Lease ending soon' };
    } else if (daysToExpiry <= 60) {
      leaseExpiryState = { severity: 'warning', badgeLabel: '2 months or less', supportingLabel: '60-day review window' };
    }
    const tenantCellBadgeClass = leaseExpiryState.severity === 'warning' ? 'bg-amber-50 text-amber-700 border-amber-200' : leaseExpiryState.severity === 'urgent' ? 'bg-orange-50 text-orange-700 border-orange-200' : leaseExpiryState.severity === 'critical' || leaseExpiryState.severity === 'expired' ? 'bg-red-50 text-red-700 border-red-200' : '';
    const leaseEndCellClass = leaseExpiryState.severity === 'normal' ? 'text-slate-700' : leaseExpiryState.severity === 'warning' ? 'rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-amber-800' : leaseExpiryState.severity === 'urgent' ? 'rounded-lg border border-orange-200 bg-orange-50 px-2.5 py-2 text-orange-800' : leaseExpiryState.severity === 'critical' || leaseExpiryState.severity === 'expired' ? 'rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-red-800' : 'text-slate-400';
    const showExpiryBadge = ['warning', 'urgent', 'critical', 'expired'].includes(leaseExpiryState.severity);

    return [
      <div title={`Open tenant details for ${tenantDisplayName}`}>
        <div className="font-semibold text-slate-900">{tenantDisplayName}</div>
        <div className="text-xs text-slate-500">{item.tenant.email}</div>
        {showExpiryBadge && (
          <span className={`mt-1.5 inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${tenantCellBadgeClass}`}>
            {leaseExpiryState.badgeLabel}
          </span>
        )}
      </div>,
      item.property?.name || '-',
      item.space?.name || 'Whole Property',
      calculations.formatDateAU(item.lease?.startDate),
      <div className={leaseEndCellClass}>
        {hasValidLeaseEnd ? (
          <>
            <div>{calculations.formatDateAU(item.lease.endDate)}</div>
            <div className="text-xs">
              {daysToExpiry < 0 ? `Expired ${Math.abs(daysToExpiry)} day${Math.abs(daysToExpiry) === 1 ? '' : 's'} ago` : daysToExpiry === 0 ? 'Today' : `${daysToExpiry} days`}
            </div>
            {leaseExpiryState.supportingLabel && leaseExpiryState.severity !== 'normal' && (
              <div className="text-xs mt-0.5">{leaseExpiryState.supportingLabel}</div>
            )}
          </>
        ) : (
          <div className="text-xs">No end date</div>
        )}
      </div>,
      calculations.formatCurrencyAUD(item.lease?.weeklyRent || 0) + '/wk',
      calculations.formatCurrencyAUD(item.lease?.bondAmount || 0),
      item.tenure,
      <div className="flex flex-col items-start gap-1"><Badge label={item.tenant.status} status={item.tenant.status} /><span className={`text-[11px] font-medium ${item.currentLease ? 'text-emerald-700' : item.nextLease ? 'text-blue-700' : 'text-slate-500'}`}>{item.currentLease ? 'Current lease' : item.nextLease ? 'Future signed lease' : 'Historical lease'}</span>{item.currentLease && item.nextLease && <span className="text-[10px] text-blue-600">Next: {calculations.formatDateAU(item.nextLease.startDate)}</span>}</div>,
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={(e)=> {e.stopPropagation();navigate(`/tenants/${item.tenant.id}`);}} className="text-amber-600 hover:underline text-xs font-medium">View Details</button>
        {item.currentLease && String(item.currentLease.id)===String(item.lease?.id) && (
          <button
            type="button"
            onClick={(e)=> {e.stopPropagation();openTerminationModal(item);}}
            className="text-red-600 hover:underline text-xs font-medium"
          >
            Terminate Early
          </button>
        )}
      </div>
    ];
  };

  const renderTenantCard=(item)=> {
    const tenantDisplayName=getTenantDisplayName(item.tenant);
    const initials=tenantDisplayName.split(/\s+/).filter(Boolean).slice(0,2).map(part=> part[0]?.toUpperCase() || '').join('') || 'T';
    const hasValidLeaseEnd=Boolean(item.lease?.endDate && !Number.isNaN(new Date(item.lease.endDate).getTime()));
    const daysToExpiry=hasValidLeaseEnd ? item.daysToExpiry : null;
    const severity=daysToExpiry===null ? 'missing' : daysToExpiry < 0 ? 'expired' : daysToExpiry===0 ? 'critical' : daysToExpiry<=30 ? 'urgent' : daysToExpiry<=60 ? 'warning' : 'normal';
    const expiryClass=severity==='warning'
      ? 'border-amber-200 bg-amber-50 text-amber-800'
      : severity==='urgent'
        ? 'border-orange-200 bg-orange-50 text-orange-800'
        : severity==='critical' || severity==='expired'
          ? 'border-red-200 bg-red-50 text-red-800'
          : 'border-slate-200 bg-slate-50 text-slate-700';
    const expiryLabel=daysToExpiry===null
      ? 'No end date'
      : daysToExpiry < 0
        ? `Expired ${Math.abs(daysToExpiry)} day${Math.abs(daysToExpiry)===1 ? '' : 's'} ago`
        : daysToExpiry===0
          ? 'Expires today'
          : `${daysToExpiry} days remaining`;
    const primaryContact=item.tenant.email || item.tenant.phone || 'No contact details';
    const leaseStartLabel=item.lease?.startDate ? calculations.formatDateAU(item.lease.startDate) : 'Not recorded';

    return (
      <article key={item.tenant.id} className="flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md">
        <button type="button" onClick={()=> navigate(`/tenants/${item.tenant.id}`)} className="block min-w-0 flex-1 p-3.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500 sm:p-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-sm font-bold text-white shadow-sm sm:h-12 sm:w-12">{initials}</div>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate text-[15px] font-bold leading-5 text-slate-900 sm:text-base">{tenantDisplayName}</h3>
                  <p className="mt-0.5 truncate text-[11px] leading-4 text-slate-500 sm:text-xs">{primaryContact}</p>
                </div>
                <div className="shrink-0"><Badge label={item.tenant.status} status={item.tenant.status} /></div>
              </div>
              <div className="mt-2 flex min-w-0 items-center gap-1.5 text-[11px] text-slate-500">
                <SafeIcon icon="Home" className="shrink-0 text-slate-400" />
                <span className="truncate font-medium text-slate-700">{item.property?.name || 'No property linked'}</span>
                <span className="shrink-0 text-slate-300">·</span>
                <span className="truncate">{item.space?.name || 'Whole Property'}</span>
              </div>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2 sm:mt-4">
            <div className="min-w-0 rounded-xl bg-emerald-50 px-3 py-2.5">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-emerald-600 sm:text-[10px]">{item.currentLease ? 'Current rent' : item.nextLease ? 'Next contracted rent' : 'Recorded rent'}</div>
              <div className="mt-1 truncate text-sm font-bold text-emerald-800">{calculations.formatCurrencyAUD(item.lease?.weeklyRent || 0)}/wk</div>
              <div className="mt-0.5 truncate text-[10px] text-emerald-700 sm:text-[11px]">{item.lease?.paymentFrequency || 'Weekly'} payments</div>
            </div>
            <div className={`min-w-0 rounded-xl border px-3 py-2.5 ${expiryClass}`}>
              <div className="text-[9px] font-semibold uppercase tracking-wide opacity-70 sm:text-[10px]">Lease end</div>
              <div className="mt-1 truncate text-sm font-bold">{hasValidLeaseEnd ? calculations.formatDateAU(item.lease.endDate) : 'Not recorded'}</div>
              <div className="mt-0.5 truncate text-[10px] sm:text-[11px]">{expiryLabel}</div>
            </div>
          </div>

          <div className="mt-2 grid grid-cols-2 gap-2">
            <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2.5">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 sm:text-[10px]">Lease started</div>
              <div className="mt-1 truncate text-xs font-semibold text-slate-800 sm:text-sm">{leaseStartLabel}</div>
              <div className="mt-0.5 truncate text-[10px] text-slate-500 sm:text-[11px]">{item.tenure || 'Tenure unavailable'}</div>
            </div>
            <div className="min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 sm:text-[10px]">Bond held</div>
              <div className="mt-1 truncate text-xs font-bold text-slate-800 sm:text-sm">{calculations.formatCurrencyAUD(item.lease?.bondAmount || 0)}</div>
              <div className="mt-0.5 truncate text-[10px] text-slate-500 sm:text-[11px]">Recorded on lease</div>
            </div>
          </div>
          {item.currentLease && item.nextLease && (
            <div className="mt-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-[10px] leading-4 text-blue-800">
              <span className="font-semibold">Next contracted lease:</span> {calculations.formatDateAU(item.nextLease.startDate)} · {calculations.formatCurrencyAUD(item.nextLease.weeklyRent || 0)}/wk
            </div>
          )}
        </button>

        <div className="grid grid-cols-1 gap-2 border-t border-slate-100 bg-slate-50/60 p-3 sm:grid-cols-2">
          <button onClick={()=> navigate(`/tenants/${item.tenant.id}`)} className="min-h-[44px] rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500">View Details</button>
          {item.currentLease && String(item.currentLease.id)===String(item.lease?.id) ? (
            <button type="button" onClick={()=> openTerminationModal(item)} className="min-h-[44px] rounded-xl border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400">Terminate Early</button>
          ) : (
            <div className="hidden sm:block" aria-hidden="true" />
          )}
        </div>
      </article>
    );
  };

  const terminationSchedulePreview=useMemo(()=> {
    if (!terminationTarget?.lease || !terminationForm.terminationDate) return {waive: 0,review: 0,preserve: 0};
    const terminationDate=toLocalDateStamp(terminationForm.terminationDate);
    if (terminationDate===null) return {waive: 0,review: 0,preserve: 0};
    return demoRepository.getRentSchedules()
      .filter(row=> String(row?.leaseId || '')===String(terminationTarget.lease.id))
      .reduce((summary,row)=> {
        const status=String(row?.status || '').trim().toLowerCase();
        const start=toLocalDateStamp(row?.periodStart);
        const end=toLocalDateStamp(row?.periodEnd);
        const hasPayment=Boolean(row?.paidToDate) || status==='paid' || status==='partial';
        const protectedStatus=['waived','archived','disputed'].includes(status);
        if (start===null || end===null || hasPayment || protectedStatus) {
          summary.preserve++;
        } else if (start > terminationDate) {
          summary.waive++;
        } else if (start <=terminationDate && end >=terminationDate) {
          summary.review++;
        } else {
          summary.preserve++;
        }
        return summary;
      },{waive: 0,review: 0,preserve: 0});
  },[terminationTarget,terminationForm.terminationDate,refreshTrigger]);

  return (
    <div className="min-w-0 animate-fade-in">
      <PageHeader
        title="Tenants"
        subtitle="Track tenants, leases, bonds, and rent increases."
        actionLabel="Add Tenant"
        onAction={()=> setIsAddModalOpen(true)}
        compact
        mobileActionFullWidth
      />

      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:mb-5 sm:p-4">
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.5fr)_minmax(190px,0.8fr)_minmax(160px,0.65fr)_auto]">
          <label className="relative block min-w-0 sm:col-span-2 lg:col-span-1">
            <span className="sr-only">Search tenants</span>
            <SafeIcon icon="Search" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={searchTerm}
              onChange={e=> setSearchTerm(e.target.value)}
              placeholder="Search tenant, property or space"
              className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-400 focus:border-amber-400 focus:bg-white focus:ring-2 focus:ring-amber-500"
            />
          </label>

          <label className="block min-w-0">
            <span className="sr-only">Filter by property</span>
            <select value={propertyFilter} onChange={e=> setPropertyFilter(e.target.value)} className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-500">
              <option value="All">All Properties</option>
              {properties.map(p=> <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>

          <label className="block min-w-0">
            <span className="sr-only">Filter by tenant status</span>
            <select value={statusFilter} onChange={e=> setStatusFilter(e.target.value)} className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-500">
              <option value="All">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Prospective">Prospective</option>
              <option value="Former">Former</option>
              <option value="Archived">Archived</option>
            </select>
          </label>

          <button
            type="button"
            onClick={()=> {setSearchTerm('');setPropertyFilter('All');setStatusFilter('Active');}}
            className="min-h-[44px] rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 sm:col-span-2 lg:col-span-1"
          >
            Clear
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 pt-3 text-[11px] leading-4 text-slate-500">
          <span className="font-semibold text-slate-700">{tenantsWithLeases.length} tenant{tenantsWithLeases.length===1 ? '' : 's'} shown</span>
          <span>{tenantListSummary.activeLeases} active lease{tenantListSummary.activeLeases===1 ? '' : 's'}</span>
          <span className={tenantListSummary.endingSoon > 0 ? 'font-medium text-amber-700' : ''}>{tenantListSummary.endingSoon} ending within 60 days</span>
          {tenantListSummary.withoutLease > 0 && <span>{tenantListSummary.withoutLease} without a linked lease</span>}
        </div>
      </div>

      <div className="hidden 2xl:block">
        <DataTable
          columns={columns}
          data={tenantsWithLeases}
          renderRow={renderRow}
          onRowClick={(item) => {
            if (item?.tenant?.id) {
              navigate(`/tenants/${item.tenant.id}`);
            }
          }}
          emptyMessage="No tenants found matching your criteria."
        />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:hidden">
        {tenantsWithLeases.length > 0
          ? tenantsWithLeases.map(renderTenantCard)
          : <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-12 text-center text-sm text-slate-500 md:col-span-2 xl:col-span-3">No tenants found matching your criteria.</div>}
      </div>

      <Modal isOpen={isAddModalOpen} onClose={()=> !isSavingTenant && setIsAddModalOpen(false)} title="Add New Tenant" maxWidth="max-w-4xl" mobileFullScreen>
        <TenantForm properties={properties} spaces={allSpaces} onSubmit={handleAddTenant} onCancel={()=> setIsAddModalOpen(false)} isSubmitting={isSavingTenant} />
      </Modal>

      <Modal isOpen={isTerminateModalOpen} onClose={closeTerminationModal} title="Terminate Lease Early" maxWidth="max-w-2xl" mobileFullScreen>
        <form onSubmit={handleTerminateLease} className="space-y-4 sm:space-y-5">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-900 sm:p-4">
            <div className="font-semibold">This keeps the complete rental history.</div>
            <div className="mt-1 leading-5 text-amber-800">Paid and historical periods are preserved. Future unpaid periods after the termination date will be waived, and the final overlapping period will be flagged for review rather than automatically prorated.</div>
          </div>

          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 sm:gap-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs text-slate-500">Tenant</div>
              <div className="break-words font-semibold text-slate-900">{terminationTarget ? `${terminationTarget.tenant.firstName || ''} ${terminationTarget.tenant.lastName || ''}`.trim() : '-'}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs text-slate-500">Property / Space</div>
              <div className="break-words font-semibold text-slate-900">{terminationTarget?.property?.name || '-'}</div>
              <div className="break-words text-xs text-slate-500">{terminationTarget?.space?.name || 'Whole Property'}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs text-slate-500">Original contractual end</div>
              <div className="font-semibold text-slate-900">{calculations.formatDateAU(terminationTarget?.lease?.endDate)}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs text-slate-500">Schedule impact</div>
              <div className="font-semibold text-slate-900">{terminationSchedulePreview.waive} future waived</div>
              <div className="text-xs leading-4 text-slate-500">{terminationSchedulePreview.review} final period review · {terminationSchedulePreview.preserve} preserved</div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Actual termination date *</label>
              <SmartDateInput required value={terminationForm.terminationDate} onChange={e=> setTerminationForm(prev=> ({...prev,terminationDate: e.target.value}))} className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Reason *</label>
              <select required value={terminationForm.reason} onChange={e=> setTerminationForm(prev=> ({...prev,reason: e.target.value}))} className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
                <option value="Lease broken by tenant">Lease broken by tenant</option>
                <option value="Mutual agreement">Mutual agreement</option>
                <option value="Tenant requested early termination">Tenant requested early termination</option>
                <option value="Owner / agent agreement">Owner / agent agreement</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Settlement amount ($)</label>
              <input type="number" min="0" step="0.01" value={terminationForm.settlementAmount} onChange={e=> setTerminationForm(prev=> ({...prev,settlementAmount: e.target.value}))} placeholder="Optional" className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Settlement payment date</label>
              <SmartDateInput value={terminationForm.settlementDate} onChange={e=> setTerminationForm(prev=> ({...prev,settlementDate: e.target.value}))} disabled={!Number(terminationForm.settlementAmount || 0)} className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:bg-slate-50 disabled:text-slate-400" baseDate={terminationForm.terminationDate} baseLabel="the termination date" />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Notes</label>
            <textarea rows="3" value={terminationForm.notes} onChange={e=> setTerminationForm(prev=> ({...prev,notes: e.target.value}))} placeholder="Optional termination or settlement notes..." className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs leading-5 text-slate-600">
            On confirmation: the lease becomes <strong>Terminated</strong>, the tenant becomes <strong>Former</strong>, future unpaid schedule rows are <strong>Waived</strong>, and any settlement is stored as a separate paid <strong>Other Income</strong> transaction linked to this property, tenant and lease. The original contractual end date is preserved in the lease notes.
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end sm:gap-3">
            <button type="button" onClick={closeTerminationModal} disabled={isTerminating} className="min-h-[44px] w-full rounded-xl px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50 sm:w-auto">Cancel</button>
            <button type="submit" disabled={isTerminating} className="min-h-[44px] w-full rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 sm:w-auto">{isTerminating ? 'Terminating Lease...' : 'Confirm Early Termination'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default TenantsPage;